import { Injectable } from '@angular/core';
import { Http, Headers } from '@angular/http';
import { Observable, Observer, BehaviorSubject } from 'rxjs';
import { Product, ProductResponse } from '../models/Product';
import { AuthService } from '../services/auth.service';
import { HttpHelperService } from "../services/utility/http-helper.service";

@Injectable()
export class ProductService {
  product$: BehaviorSubject<Product[]> = new BehaviorSubject<Product[]>([]);

  constructor(
    private http:Http,
    private authService:AuthService,
    private httpHelperService:HttpHelperService,
  ) { }


  //==========================
  //  HTTP Calls to REST API

  //GET - Http call to Products REST API, returns an observable "productResponse".
  private http_getProducts():Observable<ProductResponse>{
    //Fetch products from REST API
    // console.log('** products do not yet exist; fetching from rest api...');
    let headers = new Headers();
    //*note - map itself returns an observable of the results of the map.  Which is why you can return the map.
    let apipath = '/api/products/';
    let apiurl = this.httpHelperService.urlFromPath(apipath);  //baseurl + hostname + producturl
    //*note - the webserver will truncate '/api' from the path, and forward the remainder over to the webserver running at the designated port.
    //  Thus, the NodeJS app server won't see the '/api' portion of the path.  It will appear as though you have requested 'localhost:port/products/'
    //  Also, it is necessary to get the path from httpHelperService so that the url will work in the project when hosted at both the local and remote server locations.
    return this.http.get(apiurl,  {headers: headers})
    .map(res => {
      // console.log('ps: received products');
      let productResponse = res.json() as ProductResponse;
      //convert productResponse.products to defacto Product[] (to attach instance methods)
      productResponse.products = Product.fromJsonList(productResponse.products); 
      productResponse.products.sort(Product.productSortFunction);
      this.product$.next(productResponse.products);
      return productResponse;
    })
    .catch((error:any) => {
      //...errors if any
      console.log('product.service error', error);
       let errResponse = error.json() as ProductResponse;
       if(error.status == 0){ errResponse.msg = "Get products failed.  Unable to connect to server."; }
       else if(error.status >=400 && error.status < 500){
         errResponse.msg = "Get products failed.  No products were returned by the server.";
       } else if(error.status >= 500){
         errResponse.msg = "Get products failed.  An error occured on the server.";
       }
       return Observable.of(errResponse);
     });
    
  }

  //ADD - Http call to Products REST API, returns an observable "productResponse".
  private http_addProduct(sku:string, newProduct:Product):Observable<ProductResponse>{
    console.log('product to be added', newProduct);
    //Post product edits to Products REST API
    let headers = new Headers();
    headers.append('Authorization', this.authService.authToken);  //TODO: attach token for authorization of route
    headers.append('Contnet-Type', 'applications/json');
    // console.log('headers', headers);
    let apipath = '/api/products/admin/detail/add/';
    let apiurl = this.httpHelperService.urlFromPath(apipath);
    return this.http.post(apiurl, newProduct, {headers: headers})
    .map(res => {
      let productResponse = res.json() as ProductResponse;
      //convert productResponse.products to defacto Product[]
      productResponse.product = Product.fromJson(productResponse.product);  //fresh defacto product
      console.log('ps subscribe: res', productResponse);
      //Update this.product$ with the new product replacing the original
      if(productResponse.success){
        //Use a filter to get a FRESH array of products with 'originalProduct' removed,
        let updatedProducts = this.product$.value.filter((product) => {
          return product.sku != sku;
        });
        // console.log('updatedProducts', updatedProducts);
        //add the new product
        updatedProducts.push(productResponse.product);
        updatedProducts.sort(Product.productSortFunction);
        this.product$.next(updatedProducts);
      }
      return productResponse;
    }) 
    .catch((error:any) => {
      //...errors if any
      console.log('product.service error', error);
      let errResponse = error.json() as ProductResponse;
      if(error.status == 0){ errResponse.msg = "Product edit failed.  Unable to connect to server."; }
      else if(error.status >=400 && error.status < 500){
        errResponse.msg = "Product edit failed.  Please check the fields you are trying to edit.";
      } else if(error.status >= 500){
        errResponse.msg = "Product edit failed.  An error occured on the server.";
      }
      return Observable.of(errResponse);
    });
  };

  //EDIT - Http call to Products REST API, returns an observable "productResponse".
  private http_editProduct(id:number, productMods:{}):Observable<ProductResponse>{
    console.log('product to be saved', productMods);
    //Post product edits to Products REST API
    let headers = new Headers();
    headers.append('Authorization', this.authService.authToken);  //TODO: attach token for authorization of route
    headers.append('Contnet-Type', 'applications/json');
    // console.log('headers', headers);let apipath = '/api/products/admin/detail/add/';
    let apipath = '/api/products/admin/detail/edit/'+id;
    let apiurl = this.httpHelperService.urlFromPath(apipath);
    return this.http.patch(apiurl, productMods, {headers: headers})
    .map(res => {
      // console.log('raw prodResponse', res);
      let productResponse = res.json() as ProductResponse;
      // console.log('typecase prodResponse', productResponse);
      //convert productResponse.products to defacto Product[]
      productResponse.product = Product.fromJson(productResponse.product);  //fresh defacto product
      console.log('ps subscribe: res', productResponse);
      //Update this.product$ with the new product replacing the original
      if(productResponse.success){
        //Use a filter to get a FRESH array of products with 'originalProduct' removed,
        let updatedProducts = this.product$.value.filter((product) => {
          return product.product_id != id;
        });
        // console.log('updatedProducts', updatedProducts);
        updatedProducts.push(productResponse.product);
        updatedProducts.sort(Product.productSortFunction);
        this.product$.next(updatedProducts);
      }
      return productResponse;
    }) 
    .catch((error:any) => {
      //...errors if any
       let errResponse = error.json() as ProductResponse;
       console.log('product.service error', error);
       if(error.status == 0){ errResponse.msg = "Product edit failed.  Unable to connect to server."; }
       else if(error.status >=400 && error.status < 500){
         errResponse.msg = "Product edit failed.  Please check the fields you are trying to edit.";
       } else if(error.status >= 500){
         errResponse.msg = "Product edit failed.  An error occured on the server.";
       }
       return Observable.of(errResponse);
     });
  }

  //DELETE - Http call to Products REST API, returns an observable "productResponse".
  private http_deleteProduct(id:number):Observable<ProductResponse>{
    // console.log('product# to delete', id);
    //Post product edits to Products REST API
    let headers = new Headers();
    let apipath = '/api/products/admin/detail/delete/'+id;
    let apiurl = this.httpHelperService.urlFromPath(apipath);
    return this.http.delete(apiurl, {headers: headers})
    .map(res => {
      let productResponse = res.json() as ProductResponse;
      // console.log('ps subscribe: res', productResponse);
      //Use a filter to get a FRESH array of products, and then use it to update product$ with 'next' 
      let filteredProducts = this.product$.value.filter((product) => {
        return product.product_id != id;
      });
      this.product$.next(filteredProducts);
      return productResponse;
    })
    .catch((error:any) => {
      //...errors if any
      let errResponse = error.json() as ProductResponse;
      if(error.status == 0){ errResponse.msg = "Product edit failed.  Unable to connect to server."; }
      else if(error.status >=400 && error.status < 500){
        errResponse.msg = "Product edit failed.  Please check that the product you are trying to delete has not yet been deleted.";
      } else if(error.status >= 500){
        errResponse.msg = "Product edit failed.  An error occured on the server.";
      }
      return Observable.of(errResponse);
    });
  }


  //====================
  //  Public methods 

  //returns observable of 'productResponse' to an http call to REST API
  public loadAllProducts(allowCached: boolean){
    if (!allowCached || this.product$.value.length == 0){
      console.log('loadproducts: cache empty.  fetching...');
      let getProduct$ = this.http_getProducts();
      return getProduct$;
    } else{
      console.log('loadproducts: returning cached.');
      let productResponse:ProductResponse = {success:true, msg:'Products already loaded.  Used Cached.', products:this.product$.value}
      return Observable.of(productResponse);
    }
  }

  //returns a new 'blank' product
  public blankProduct():Product {
    let product = new Product();
    return product;
  }

  public knownCategories() {
    let allproducts = this.product$.value;
    let categories = allproducts.map(product => product.category);
    let uniquecategories = Array.from(new Set(categories));
    return uniquecategories;
  }

  public knownUnitInfos() {
    let allproducts = this.product$.value;
    let unitInfos = allproducts.map(product => product.product_unit_info);
    let uniqueUnitInfos = Array.from(new Set(unitInfos));
    return uniqueUnitInfos;
  }

  public checkSkuExists(sku:string):boolean{
    let allproducts = this.product$.value;
    
    //guard against null sku
    if(!sku){ return false; }
    
    //filter on sku, check if a product has that sku
    let filteredproducts = allproducts.filter((product) => {
      return product.sku == sku;
    });
    let skuExists = (filteredproducts.length > 0)
    return skuExists;
    
  }


  //returns observable of 'productResponse' to an http call to REST API
  public addProduct(newProduct: Product):Observable<ProductResponse>{
    let sku = newProduct.sku;
    //confine edits to properties of originalProduct
    // let originalProduct = this.getProductBySku(sku);
    // let allowedProperties = Object.keys(originalProduct);
    // let reducedNewProduct:Product = newProduct.reduceToAllowedProperties(allowedProperties) as Product;
    //collect differences from original
    // let mods = originalProduct.differencesWithEditedProduct(reducedNewProduct);

    if (Object.keys(newProduct).length === 0){
      // console.log('no changes to product');
      //construct a productResponse reporting that no changes detected.
      let productResponse:ProductResponse = {success:false, msg:'Did not detect any changes.  Product was not saved.', products:null}
      return Observable.of(productResponse);
    } else {
      let addProduct$ = this.http_addProduct(sku, newProduct);
      return addProduct$;
    }
  }
  

  //returns observable of 'productResponse' to an http call to REST API
  public editProduct(editedProduct: Product):Observable<ProductResponse>{
    let id = editedProduct.product_id;
    //confine edits to properties of originalProduct
    let originalProduct = this.getProductById(id);
    let allowedProperties = Object.keys(originalProduct);
    let reducedEditedProduct:Product = editedProduct.reduceToAllowedProperties(allowedProperties) as Product;
    //collect differences from original
    let mods = originalProduct.differencesWithEditedProduct(reducedEditedProduct);

    if (Object.keys(mods).length === 0){
      // console.log('no changes to product');
      //construct a productResponse reporting that no changes detected.
      let productResponse:ProductResponse = {success:false, msg:'Did not detect any changes.  Product was not saved.', products:null}
      return Observable.of(productResponse);
    } else {
      let editProduct$ = this.http_editProduct(id, mods);
      return editProduct$;
    }
  }

  //returns observable of 'productResponse' to an http call to REST API
  public deleteProduct(productToDelete: Product):Observable<ProductResponse>{
    let id = productToDelete.product_id;
    let originalProduct = this.getProductById(id);
    //create/return observable stream
    let deleteProduct$ = this.http_deleteProduct(id);
    return deleteProduct$;
  }

  //**products must be loaded beforehand.
  //returns products within category, or allproducts if category is null
  public getProductsByCategory(category?:string):Product[]{
    //filter by category
    let allproducts = this.product$.value;
    if(category){
      // console.log('filtering products on category', category);
      let filteredproducts = allproducts.filter((product) => {
        return product.category == category;
      });
      return filteredproducts; 
    } else {
      // console.log('null category, returning all products');
      return allproducts;
    }
  }
  
  //**products must be loaded beforehand.
  //alternative option: getproducts by http, then filter on the product sku
  public getProductBySku(sku:string):Product{
    // console.log('getProductBySku was called with product$.value', this.product$.value);
    //filter by sku
    let filteredProducts = this.product$.value.filter((product) => {
      return product.sku == sku;
    });
    let product = filteredProducts[0];
    return product; 
  }

  //**products must be loaded beforehand
  public getProductById(id:number):Product{
    let filteredProducts = this.product$.value.filter((product) => {
      return product.product_id == id;
    });
    let product = filteredProducts[0];
    return product;
  }
}


//Unused Code

    // let headers = new Headers();
    // let observableResponse = this.http.get('http://localhost:3000/products/',  {headers: headers})
    // .map(res => res.json()).subscribe((response:ProductResponse) => {
    //   console.log('productResponse: ', response);

    //   let productlist = Product.fromJsonList(response.products); //convert service observable to product[]
    //   //convert to observable products
    //   // let observableproducts = productlist.map((product) => {
    //   //   return Observable.of(product);
    //   // })
    //   // console.log('...updating observable products')
    //   this.products$ = Observable.of(productlist);
      
    //   // console.log('observable products: ', productlist);
    //   //   let product = this.products.filter((product) => {
    //   //     return product.sku == this.sku;
    //   //   });

    // });


  // getProducts(): Observable<Product[]>{
  //   console.log('product-service: inside getproducts()');
  //   return this.products$;
  // }

  // getProducts(callback){
  //   return this.products;
  // }


  // getProducts(): Promise<Product[]> {
  //   return Promise.resolve(this.products);
  // }

  // getProducts(){
  //   // console.log('getting products');
  //   // let headers = new Headers();
  //   // return this.http.get('http://localhost:3000/products/',  {headers: headers})
  //   // .map(res => res.json());
  //   return this.products;
  // }

  // console.log('product service init.  calling http to get products...');
  //console.log('productResponse: ', response);
  // console.log('product sku: ', sku);
  // console.log('filteredproducts: ', filteredproducts);
  // console.log('product: ', product);

  //You could return cached products as a quick and dirty observable, so that you don't have to 'complete' the observable.
  // if(this.products){
  //   // console.log('## returning existing products');
  //   let products$ = Observable.of(this.products);
  //   console.log('returning cached');
  //   return products$;
  // }

  //  - This is how you would 'getProducts' as a promise.
  //  - Note - use Observables in place of promises, since you can observe 'streams' (aka more than one update),
  //           and you can cancel them as well.
  // getProducts():Promise<Product[]>{
  //   //wrap getProducts around an Promise to make it async.
  //   let productsPromise = new Promise<Product[]>((resolve, reject) => {
  //     //return products if it was previously fetched
  //     if(this.products){
  //       console.log('## returning existing products');
  //       resolve(this.products);
  //     }
  //     //Fetch products from REST API
  //     console.log('** products do not yet exist; fetching from rest api...');
  //     let headers = new Headers();
  //     this.http.get('http://localhost:3000/products/',  {headers: headers})
  //     .map(res => res.json()).subscribe((response:ProductResponse) => {
  //       console.log('productResponse: ', response);
  //       let productlist = Product.fromJsonList(response.products); //convert service observable to product[]
  //       this.products = productlist;
        
  //       resolve(productlist);
  //     });
  //   }); 
  //   return productsPromise;
  // }

  // import 'rxjs/add/operator/map';  //not needed, b/c map is part of rxjs observable


  // //  This works just as well if you want to create observables using 'Observable.of(...)'.
  // //   Observable.of will auto-call observer.complete for you.
  // getProducts():Observable<Product[]>{
  //   //wrap getProducts around an Observable to make it async.
    
  //     //return cached products if already fetched
  //     if(this.products){
  //       console.log('## returning existing products');
  //       let productsStream$ = Observable.of(this.products);
  //       return productsStream$;
  //     }
  //     //Fetch products from REST API
  //     // console.log('** products do not yet exist; fetching from rest api...');
  //     let headers = new Headers();
  //     //*note - map itself returns an observable of the results of the map.  Which is why you can return the map.
  //     let productsStream$ = this.http.get('http://localhost:3000/products/',  {headers: headers})
  //     .map(res => {
  //       let response:ProductResponse = res.json();
  //       let productlist = Product.fromJsonList(response.products); //convert service observable to product[]
  //       this.products = productlist;
  //       return productlist;
  //     });  

  //     return productsStream$;
  // }

  //  

    // //This works, and wraps the entire functionality of getProducts(),
  // // so that you don't have to create/capture observables from 3rd parties.
  // // *I think this way is cleaner than below where I created/captured observables.
  // getProducts():Observable<Product[]>{
  //   //wrap getProducts around an Observable to make it async.
  //   let productsObservable$ = Observable.create((observer: Observer<Product[]>) => {
  //     //return cached products if already fetched
  //     if(this.products){
  //       // console.log('## returning existing products');
  //       observer.next(this.products);
  //       return observer.complete();
  //     }
  //     //Fetch products from REST API
  //     // console.log('** products do not yet exist; fetching from rest api...');
  //     let headers = new Headers();
  //     //*note - map itself returns an observable of the results of the map.  Which is why you can return the map.
  //     this.http.get('http://localhost:3000/products/',  {headers: headers})
  //     .map(res => res.json()).subscribe((response:ProductResponse) => {
  //       let productlist = Product.fromJsonList(response.products); //convert service observable to product[]
  //       this.products = productlist;
  //       observer.next(productlist);
  //       return observer.complete();
  //     });
  //   }); 
  //   return productsObservable$;
  // }

  //   getProductBySku(sku:string):Observable<Product>{
//     //wrap in Observable for async.
//     let productObserver$ = Observable.create((observer: Observer<Product>) => {
//       //getproducts, then filter on the product sku
//       this.getProducts().subscribe(products => {
//         // console.log('fetching product');
//         //filter by sku
//         let filteredproducts = products.filter((product) => {
//           return product.sku == sku;
//         });
//         let product = filteredproducts[0];
//         observer.next(product);
//       });
//     });
//     return productObserver$;
//   }
// }

  //Replaced by BehaviorSubject
  // //Simplified Get.  Offloaded processing to http_getProducts.
  // // Returns an observable stream that emits Product[] and immediately completes.
  // getProducts():Observable<Product[]>{
  //   //return cached products if already fetched.  Otherwise, fetch them from http.
  //   let productsStream$;
  //   if(this.product$.value){
  //     // console.log('** returning existing products');
  //     //this does not actually observe the products variable.  
  //     //It's just a wrapper for it so that the return value is consistent.
  //     productsStream$ = Observable.of(this.products);  
  //     return productsStream$;
  //   } else {
  //     productsStream$ = this.http_getProducts();
  //     return productsStream$;
  //   }
  // }

  //The old way returns an observable.
  //  The new way executes the request and stores the value in a 'behaviorSubject'
  // //Http call to Products REST API, returns an observable "productsStream".
  // http_getProducts():Observable<Product[]>{
  //   //Fetch products from REST API
  //   // console.log('** products do not yet exist; fetching from rest api...');
  //   let headers = new Headers();
  //   //*note - map itself returns an observable of the results of the map.  Which is why you can return the map.
  //   let productsStream$ = this.http.get('http://localhost:3000/products/',  {headers: headers})
  //   .map(res => {
  //     let response = res.json() as ProductResponse;
  //     let productlist = Product.fromJsonList(response.products); //convert service observable to product[]
  //     this.products = productList;
  //     return productlist;
  //   });
  //   return productsStream$;
  // }

  // mods['product_id'] = id;
  // console.log('mods to save', mods);


//  //NO! - Splicing modifies the actual array.  We don't want to do that, because then we can't issue .next() for some dumb reason.
//  Use a filter instead.
  // if(productResponse.success){
//   console.log('Delete was successful.');
//   // TODO: update products by removing the deletedproduct         
//   let products:Product[] = this.product$.value;
//   var index = products.indexOf(originalProduct);
//   console.log('index of deleted product', index);
  
//   // let products = this.product$.value;
//   // console.log('this.products', products);

//   // console.log('original product to delete', originalProduct);
//   // let i = products.indexOf(originalProduct);
//   // console.log('index of product to delete', i);
//   // let p = products[i];
//   // console.log('product at that index', p);
//   console.log('will delete from product$');
//   if (index > -1) {
//     console.log('found product at index', index);
//      products.splice(index, 1);
//      console.log('spliced products', products);
//      console.log('type of products', typeof products);
//     //  this.product$.next(products);
//     console.log('next for products');
//     // Product.fromJsonList(productResponse.products);
//   }
//   console.log('did delete from product$');
// }

// This worked, however I came up with better solution, having edit product REST
// send back the edited product w/ new timestamps.  Then replacing object in products behaviorSubject.
// let modProperties = Object.keys(mods);
// for (let property of modProperties){
//   console.log('mod assigning '+property+' to original');
//   originalProduct[property] = mods[property];
// }


// WEBPACK FOOTER //
// ./src/app/services/product.service.ts